import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./AdminPage.module.scss";
import proxyUrl from "../../utils/proxyUrl";

/* ───────── Поля товара ───────── */
const PHOTO_FIELDS = ["Фото", "ДопФото", "ДопФото2", "ДопФото3", "ДопФото4"];
const PHOTO_LABELS = [
  "Главное фото",
  "Доп. фото 1",
  "Доп. фото 2",
  "Доп. фото 3",
  "Доп. фото 4",
];

const EMPTY_FORM = {
  Товар: "",
  Цена: "",
  Наличие: "Да",
  Количество: "",
  Фильтр: "",
  ДопФильтр: "",
  Описание: "",
  Материалы: "",
  Уход: "",
  Доставка: "",
  Фото: "",
  ДопФото: "",
  ДопФото2: "",
  ДопФото3: "",
  ДопФото4: "",
};

/* ───────── Helpers ───────── */
function getToken() {
  return sessionStorage.getItem("adminToken");
}
function setToken(t) {
  sessionStorage.setItem("adminToken", t);
}
function clearToken() {
  sessionStorage.removeItem("adminToken");
}

async function apiFetch(url, opts = {}) {
  const token = getToken();
  const headers = {
    ...(opts.body instanceof FormData
      ? {}
      : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...opts.headers,
  };
  const res = await fetch(url, { ...opts, headers });
  if (res.status === 401) {
    clearToken();
    throw new Error("Unauthorized");
  }
  return res;
}

/* ══════════════════════════════════
   LOGIN  FORM
   ══════════════════════════════════ */
function LoginForm({ onLogin }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Ошибка входа");
        return;
      }
      setToken(data.token);
      onLogin();
    } catch {
      setError("Ошибка соединения с сервером");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginWrapper}>
      <form className={styles.loginCard} onSubmit={handleSubmit}>
        <h1>Панель управления</h1>
        <p className={styles.loginSubtitle}>Войдите, чтобы продолжить</p>

        {error && <div className={styles.errorMsg}>{error}</div>}

        <input
          type="text"
          placeholder="Логин"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          autoFocus
          required
        />
        <input
          type="password"
          placeholder="Пароль"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit" disabled={loading}>
          {loading ? "Вход..." : "Войти"}
        </button>
      </form>
    </div>
  );
}

/* ══════════════════════════════════
   PHOTO  UPLOAD  SLOT
   ══════════════════════════════════ */
function PhotoSlot({ label, url, onUpload, onDelete, uploading }) {
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onUpload(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className={styles.photoSlot}>
      <span className={styles.photoLabel}>{label}</span>

      {url ? (
        <div className={styles.photoPreview}>
          <img
            src={url.startsWith("/") ? url : proxyUrl(url)}
            alt={label}
          />
          <button
            type="button"
            className={styles.photoDeleteBtn}
            onClick={onDelete}
            title="Удалить фото"
          >
            ✕
          </button>
        </div>
      ) : (
        <div
          className={styles.photoDropArea}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (
            <span>Загрузка…</span>
          ) : (
            <>
              <span className={styles.photoPlus}>+</span>
              <span>Загрузить</span>
            </>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleChange}
      />

      {url && (
        <button
          type="button"
          className={styles.photoChangeBtn}
          onClick={() => inputRef.current?.click()}
        >
          Заменить
        </button>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   PRODUCT  FORM  (add / edit)
   ══════════════════════════════════ */
function ProductForm({ product, onSave, onCancel }) {
  const [form, setForm] = useState(() =>
    product ? { ...EMPTY_FORM, ...product } : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);

  const set = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /* ── Загрузка фото ── */
  const handlePhotoUpload = async (field, file) => {
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const res = await apiFetch("/api/admin/upload", {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (res.ok) set(field, data.url);
    } catch (e) {
      console.error("Upload error:", e);
    } finally {
      setUploadingField(null);
    }
  };

  /* ── Удаление фото ── */
  const handlePhotoDelete = async (field) => {
    const url = form[field];
    if (!url) return;

    try {
      await apiFetch("/api/admin/photo", {
        method: "DELETE",
        body: JSON.stringify({ url }),
      });
    } catch (e) {
      console.error("Delete photo error:", e);
    }
    set(field, "");
  };

  /* ── Сохранение товара ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form["Товар"].trim() || !form["Цена"].trim()) {
      alert("Название и цена обязательны");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onCancel}>
          ✕
        </button>

        <h2>{product ? "Редактирование товара" : "Новый товар"}</h2>

        <form onSubmit={handleSubmit} className={styles.productForm}>
          {/* ── Фото ── */}
          <div className={styles.photosGrid}>
            {PHOTO_FIELDS.map((field, i) => (
              <PhotoSlot
                key={field}
                label={PHOTO_LABELS[i]}
                url={form[field]}
                uploading={uploadingField === field}
                onUpload={(file) => handlePhotoUpload(field, file)}
                onDelete={() => handlePhotoDelete(field)}
              />
            ))}
          </div>

          {/* ── Основные поля ── */}
          <div className={styles.fieldsGrid}>
            <label>
              Название*
              <input
                type="text"
                value={form["Товар"]}
                onChange={(e) => set("Товар", e.target.value)}
                required
              />
            </label>

            <label>
              Цена (₽)*
              <input
                type="text"
                value={form["Цена"]}
                onChange={(e) => set("Цена", e.target.value)}
                required
              />
            </label>

            <label>
              Наличие
              <select
                value={form["Наличие"]}
                onChange={(e) => set("Наличие", e.target.value)}
              >
                <option value="Да">Да</option>
                <option value="Нет">Нет</option>
              </select>
            </label>

            <label>
              Количество
              <input
                type="text"
                value={form["Количество"]}
                onChange={(e) => set("Количество", e.target.value)}
              />
            </label>

            <label>
              Категория
              <input
                type="text"
                value={form["Фильтр"]}
                onChange={(e) => set("Фильтр", e.target.value)}
              />
            </label>

            <label>
              Подкатегория
              <input
                type="text"
                value={form["ДопФильтр"]}
                onChange={(e) => set("ДопФильтр", e.target.value)}
              />
            </label>

            <label>
              Доставка
              <input
                type="text"
                value={form["Доставка"]}
                onChange={(e) => set("Доставка", e.target.value)}
              />
            </label>
          </div>

          {/* ── Текстовые блоки ── */}
          <label className={styles.fullWidth}>
            Описание
            <textarea
              rows={3}
              value={form["Описание"]}
              onChange={(e) => set("Описание", e.target.value)}
            />
          </label>

          <label className={styles.fullWidth}>
            Материалы
            <textarea
              rows={2}
              value={form["Материалы"]}
              onChange={(e) => set("Материалы", e.target.value)}
            />
          </label>

          <label className={styles.fullWidth}>
            Рекомендации по уходу
            <textarea
              rows={2}
              value={form["Уход"]}
              onChange={(e) => set("Уход", e.target.value)}
            />
          </label>

          {/* ── Кнопки ── */}
          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>
              Отмена
            </button>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   ADMIN  DASHBOARD
   ══════════════════════════════════ */
function AdminDashboard({ onLogout }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/products");
      const data = await res.json();
      setProducts(data);
    } catch (e) {
      console.error("Load products error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  /* ── Сохранение товара ── */
  const handleSave = async (formData) => {
    try {
      if (editingProduct) {
        await apiFetch(`/api/admin/products/${editingProduct.id}`, {
          method: "PUT",
          body: JSON.stringify(formData),
        });
      } else {
        await apiFetch("/api/admin/products", {
          method: "POST",
          body: JSON.stringify(formData),
        });
      }
      setFormOpen(false);
      setEditingProduct(null);
      await loadProducts();
    } catch (e) {
      console.error("Save error:", e);
      alert("Ошибка при сохранении");
    }
  };

  /* ── Удаление товара ── */
  const handleDelete = async (product) => {
    if (!window.confirm(`Удалить «${product["Товар"]}»?`)) return;
    try {
      await apiFetch(`/api/admin/products/${product.id}`, {
        method: "DELETE",
      });
      await loadProducts();
    } catch (e) {
      console.error("Delete error:", e);
      alert("Ошибка при удалении");
    }
  };

  /* ── Импорт из Google Sheets ── */
  const handleImport = async () => {
    if (
      !window.confirm(
        "Импортировать товары из Google Sheets? Они будут ДОБАВЛЕНЫ к существующим."
      )
    )
      return;

    setImporting(true);
    try {
      const res = await apiFetch("/api/admin/import-sheets", {
        method: "POST",
      });
      const data = await res.json();
      if (res.ok) {
        alert(`Импортировано: ${data.imported} товаров`);
        await loadProducts();
      } else {
        alert("Ошибка импорта: " + (data.error || ""));
      }
    } catch (e) {
      console.error("Import error:", e);
      alert("Ошибка при импорте");
    } finally {
      setImporting(false);
    }
  };

  /* ── Открыть форму ── */
  const openAdd = () => {
    setEditingProduct(null);
    setFormOpen(true);
  };
  const openEdit = (product) => {
    setEditingProduct(product);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditingProduct(null);
  };

  /* ── Фильтрация ── */
  const filteredProducts = searchQuery
    ? products.filter(
        (p) =>
          (p["Товар"] || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (p["Фильтр"] || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : products;

  /* ── Logout ── */
  const handleLogout = async () => {
    try {
      await apiFetch("/api/admin/logout", { method: "POST" });
    } catch {}
    onLogout();
  };

  return (
    <div className={styles.dashboard}>
      {/* ── Top Bar ── */}
      <header className={styles.topBar}>
        <h1>Панель управления</h1>
        <div className={styles.topBarActions}>
          <button
            className={styles.importBtn}
            onClick={handleImport}
            disabled={importing}
          >
            {importing ? "Импорт…" : "Импорт из Google Sheets"}
          </button>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            Выйти
          </button>
        </div>
      </header>

      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        <button className={styles.addBtn} onClick={openAdd}>
          + Добавить товар
        </button>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Поиск по названию или категории…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <span className={styles.count}>
          Всего: {products.length}
        </span>
      </div>

      {/* ── Products Grid ── */}
      {loading ? (
        <div className={styles.loadingText}>Загрузка товаров…</div>
      ) : filteredProducts.length === 0 ? (
        <div className={styles.emptyText}>
          {products.length === 0
            ? "Товаров пока нет. Добавьте первый или импортируйте из Google Sheets."
            : "Ничего не найдено"}
        </div>
      ) : (
        <div className={styles.productsGrid}>
          {filteredProducts.map((product) => (
            <div key={product.id} className={styles.productCard}>
              <div className={styles.cardImage}>
                {product["Фото"] ? (
                  <img
                    src={proxyUrl(product["Фото"])}
                    alt={product["Товар"]}
                  />
                ) : (
                  <div className={styles.noPhoto}>Нет фото</div>
                )}
              </div>

              <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>{product["Товар"]}</h3>
                <p className={styles.cardPrice}>{product["Цена"]} ₽</p>

                <div className={styles.cardMeta}>
                  {product["Фильтр"] && (
                    <span className={styles.badge}>{product["Фильтр"]}</span>
                  )}
                  <span
                    className={`${styles.stockBadge} ${
                      (product["Наличие"] || "").toLowerCase() === "да"
                        ? styles.inStock
                        : styles.outOfStock
                    }`}
                  >
                    {(product["Наличие"] || "").toLowerCase() === "да"
                      ? "В наличии"
                      : "Нет в наличии"}
                  </span>
                </div>
              </div>

              <div className={styles.cardActions}>
                <button
                  className={styles.editBtn}
                  onClick={() => openEdit(product)}
                >
                  Редактировать
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => handleDelete(product)}
                >
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal Form ── */}
      {formOpen && (
        <ProductForm
          product={editingProduct}
          onSave={handleSave}
          onCancel={closeForm}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════
   MAIN  ADMIN  PAGE
   ══════════════════════════════════ */
export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setChecking(false);
      return;
    }
    apiFetch("/api/admin/check")
      .then((r) => {
        setIsLoggedIn(r.ok);
        setChecking(false);
      })
      .catch(() => {
        setIsLoggedIn(false);
        setChecking(false);
      });
  }, []);

  if (checking) {
    return <div className={styles.loadingPage}>Загрузка…</div>;
  }

  if (!isLoggedIn) {
    return <LoginForm onLogin={() => setIsLoggedIn(true)} />;
  }

  return (
    <AdminDashboard
      onLogout={() => {
        clearToken();
        setIsLoggedIn(false);
      }}
    />
  );
}
